<script setup lang="ts">
import '../office-statusbar.css';

const props = withDefaults(
  defineProps<{
    modelValue: number;
    min: number;
    max: number;
    zoomLabel: string;
    zoomOutLabel: string;
    zoomInLabel: string;
    resetLabel: string;
    step?: number;
    resetValue?: number;
  }>(),
  { step: 10, resetValue: 100 }
);

const emit = defineEmits<{
  'update:modelValue': [value: number];
}>();

function update(value: number): void {
  if (!Number.isFinite(value)) return;
  emit('update:modelValue', Math.max(props.min, Math.min(props.max, value)));
}

function onSliderInput(event: Event): void {
  update(Number((event.target as HTMLInputElement).value));
}
</script>

<template>
  <div class="nx-office-zoom" role="group" :aria-label="zoomLabel" data-testid="zoom-control">
    <!-- The buttons keep the pointer from taking focus: the zoom has to stay
         with the document, not with a button whose arrow keys do nothing.
         The slider cannot, a drag needs the press. -->
    <button
      type="button"
      class="nx-office-zoom__button nx-office-zoom__button--step"
      :title="zoomOutLabel"
      :aria-label="zoomOutLabel"
      data-testid="zoom-out"
      @mousedown.prevent
      @click="update(modelValue - step)"
    >
      −
    </button>
    <input
      type="range"
      class="nx-office-zoom__slider"
      :min="min"
      :max="max"
      :step="step"
      :value="modelValue"
      :title="zoomLabel"
      :aria-label="zoomLabel"
      data-testid="zoom-slider"
      @input="onSliderInput"
    />
    <button
      type="button"
      class="nx-office-zoom__button nx-office-zoom__button--step"
      :title="zoomInLabel"
      :aria-label="zoomInLabel"
      data-testid="zoom-in"
      @mousedown.prevent
      @click="update(modelValue + step)"
    >
      +
    </button>
    <button
      type="button"
      class="nx-office-zoom__button nx-office-zoom__button--readout"
      :title="resetLabel"
      :aria-label="resetLabel"
      data-testid="zoom-readout"
      @mousedown.prevent
      @click="update(resetValue)"
    >
      {{ modelValue }}%
    </button>
  </div>
</template>
